import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
    /** Whether the modal is visible */
    open: boolean;
    /** Called on backdrop click, Esc, and the close button */
    onClose: () => void;
    /**
     * id of the element inside `children` that names the dialog,
     * wired to `aria-labelledby`.
     */
    labelledById?: string;
    children: React.ReactNode;
}

/**
 * Lightweight, accessible modal — no external dependency.
 *
 * - Renders into a portal on `document.body` so it escapes the configurator's
 *   transformed / z-indexed stacking context.
 * - Closes on backdrop click, Esc, and the explicit close button.
 * - Moves focus into the dialog on open and restores it to the previously
 *   focused element on close.
 * - Styled with the app's design tokens (--surface / --fg / --border).
 */
export default function Modal({ open, onClose, labelledById, children }: ModalProps) {
    const closeRef = useRef<HTMLButtonElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;
        closeRef.current?.focus();

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previouslyFocused.current?.focus();
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                aria-hidden="true"
            />

            {/* Dialog */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledById}
                onClick={(e) => e.stopPropagation()}
                className={[
                    'relative w-full max-w-sm rounded-2xl p-8',
                    'border border-[var(--border)] bg-[var(--surface)]',
                    'text-[var(--fg)] shadow-2xl',
                ].join(' ')}
            >
                <button
                    ref={closeRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Schließen"
                    className={[
                        'absolute top-4 right-4 flex h-8 w-8 items-center justify-center',
                        'rounded-full text-lg text-[var(--fg-muted)] transition-colors',
                        'hover:bg-[var(--bg)] hover:text-[var(--fg)]',
                    ].join(' ')}
                >
                    ✕
                </button>
                {children}
            </div>
        </div>,
        document.body,
    );
}
